import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getPlans } from '@/api/endpoints/billing'
import type { Plan } from '@/api/types'
import {
	PRO_PLAN_FALLBACK,
	PRO_PLAN_SLUG,
	paymentSuccessDescription,
	priceLabel,
	proCta,
	proFeatures,
	proLimitsNote,
	proOnboardingFootnote,
	proPaywallDescription,
	proRetentionFeatures
} from '@/constants/pro'

/**
 * Тарифы с бэка. UI показывает один — Pro, — но список храним целиком: выбор тарифа,
 * если он появится, не должен переделывать слой данных.
 *
 * Строки собираются здесь, а не в компонентах: шесть экранов показывают одни и те же
 * цифры, и собирать их каждый по-своему — как раз тот способ, которым они разъезжаются.
 */

/**
 * Какой из тарифов — Pro. Слаг из константы важнее порядка в массиве, но и он может
 * разойтись с бэком (переименование, новый период), поэтому запасное правило — первый
 * платный тариф. Молчать о расхождении нельзя: цена на кнопке ведёт к списанию.
 */
function resolvePro(plans: Plan[]): Plan | null {
	const bySlug = plans.find((plan) => plan.slug === PRO_PLAN_SLUG)
	if (bySlug) return bySlug

	const paid = plans.find((plan) => plan.priceMinor > 0)
	if (paid) {
		console.warn(`[billing] тарифа «${PRO_PLAN_SLUG}» в ответе нет, взят первый платный: «${paid.slug}»`)
		return paid
	}
	if (plans.length) console.warn('[billing] в ответе /billing/plans нет платных тарифов')
	return null
}

export const usePlansStore = defineStore('plans', () => {
	const plans = ref<Plan[]>([])
	// Тарифы уже спрашивали в этой сессии приложения (см. ensurePlans).
	const loaded = ref(false)

	/**
	 * Тариф с сервера или null. Отдельный computed, чтобы выбор (и его запись в лог)
	 * случался один раз, а не в каждом геттере, который спрашивает про тариф.
	 */
	const serverPro = computed(() => resolvePro(plans.value))

	/**
	 * Тариф Pro. Никогда не null: без ответа сервера отдаём фолбэк, иначе экраны онбординга
	 * и пейволла придётся учить показывать пустоту, а кнопку оплаты — быть неактивной.
	 */
	const pro = computed<Plan>(() => serverPro.value ?? PRO_PLAN_FALLBACK)

	/** Тариф пришёл с сервера, а не подставлен фолбэком. Для логов и тестов, не для UI. */
	const isServerPlan = computed(() => serverPro.value !== null)

	const cta = computed(() => proCta(pro.value))
	const features = computed(() => proFeatures(pro.value))
	const paywallDescription = computed(() => proPaywallDescription(pro.value))
	const limitsNote = computed(() => proLimitsNote(pro.value))
	const onboardingFootnote = computed(() => proOnboardingFootnote(pro.value))
	const retentionFeatures = computed(() => proRetentionFeatures(pro.value))
	const successDescription = computed(() => paymentSuccessDescription(pro.value))
	const priceText = computed(() => priceLabel(pro.value))

	async function fetchPlans(): Promise<void> {
		plans.value = await getPlans()
		loaded.value = true
	}

	/**
	 * Дотягивает тарифы. Ошибку глушим: фолбэк уже показывает прежние цифры, а ронять
	 * онбординг из-за сетевого сбоя нельзя. Повторный вызов запроса не делает.
	 */
	async function ensurePlans(): Promise<void> {
		if (loaded.value) return
		try {
			await fetchPlans()
		} catch (error) {
			console.warn('тарифы не загрузились', error)
		}
	}

	return {
		plans,
		loaded,
		pro,
		isServerPlan,
		cta,
		features,
		paywallDescription,
		limitsNote,
		onboardingFootnote,
		retentionFeatures,
		successDescription,
		priceText,
		fetchPlans,
		ensurePlans
	}
})
